/**
 * planner/suggestions.ts — pure smart-suggestion rules.
 *
 * Every suggestion is derived from RECORDED planner data (plans, goals,
 * lifestyle settings) with an explicit basis. No data ⇒ no suggestion —
 * nothing is fabricated (no invented weather, prices, or flight fares;
 * those arrive only via future providers/engines through recorded state).
 */

import type {
  LifestylePlan,
  PlannedDestination,
  PlannerGoal,
  PlannerSuggestion,
  TravelPlanState,
} from './models';

const DAY = 24 * 60 * 60 * 1000;

export function computeSuggestions(input: {
  travel: TravelPlanState;
  lifestyle: LifestylePlan;
  goals: PlannerGoal[];
  now: number;
}): PlannerSuggestion[] {
  const { travel, lifestyle, goals, now } = input;
  const suggestions: PlannerSuggestion[] = [];
  const next = travel.nextDestination;

  // Departure approaching but no arranged arrival details → book housing now.
  if (next?.plannedArrival != null) {
    const daysToArrival = Math.floor((next.plannedArrival - now) / DAY);
    if (daysToArrival >= 0 && daysToArrival <= 30) {
      suggestions.push({
        id: `book-housing-${next.id}`,
        message: `Book housing in ${next.city} now — you arrive in ${daysToArrival} day${daysToArrival === 1 ? '' : 's'}.`,
        basis: [`planned arrival ${new Date(next.plannedArrival).toISOString().slice(0, 10)}`],
        severity: daysToArrival <= 14 ? 'important' : 'nudge',
      });
    }
    if (daysToArrival >= 0 && daysToArrival <= 7) {
      suggestions.push({
        id: `exchange-currency-${next.id}`,
        message: `Exchange currency this week — you move to ${next.city} within 7 days.`,
        basis: [`planned arrival ${new Date(next.plannedArrival).toISOString().slice(0, 10)}`],
        severity: 'nudge',
      });
    }
  }

  // Flexible dates on the next hop → consider waiting for a better window.
  if (next != null && next.flexibleDates && next.plannedArrival == null) {
    suggestions.push({
      id: `flexible-window-${next.id}`,
      message: `Your ${next.city} dates are flexible — you can wait for a cheaper travel window before committing.`,
      basis: ['next destination has flexible, unset dates'],
      severity: 'info',
    });
  }

  // Income goal pace within a deadline month.
  for (const goal of goals) {
    if (goal.completedAt != null || goal.targetDate == null) continue;
    const daysLeft = Math.floor((goal.targetDate - now) / DAY);
    if (daysLeft < 0) {
      suggestions.push({
        id: `goal-overdue-${goal.id}`,
        message: `"${goal.title}" passed its target date at ${Math.round((goal.currentAmount / goal.targetAmount) * 100)}% — reschedule or rescope it.`,
        basis: [`goal target date ${new Date(goal.targetDate).toISOString().slice(0, 10)}`, `progress ${goal.currentAmount}/${goal.targetAmount}`],
        severity: 'important',
      });
      continue;
    }
    const progress = goal.currentAmount / goal.targetAmount;
    if (daysLeft <= 30 && progress < 0.5) {
      suggestions.push({
        id: `goal-behind-${goal.id}`,
        message: `"${goal.title}" is ${Math.round(progress * 100)}% done with ${daysLeft} days left — plan extra missions or extend the deadline.`,
        basis: [`progress ${goal.currentAmount}/${goal.targetAmount}`, `${daysLeft} days to target date`],
        severity: 'nudge',
      });
    }
  }

  // Lifestyle balance: all work, no planned rest.
  if (lifestyle.missionsPerWeek != null && lifestyle.missionsPerWeek >= 5 && lifestyle.restDaysPerWeek === 0) {
    suggestions.push({
      id: 'plan-rest-days',
      message: 'You planned 5+ mission days and zero rest days — schedule at least one rest day per week.',
      basis: [`missionsPerWeek ${lifestyle.missionsPerWeek}`, 'restDaysPerWeek 0'],
      severity: 'nudge',
    });
  }

  // Stay-duration nudge: stay plan exists but no next destination at all.
  if (lifestyle.stayDurationMonths != null && next == null && travel.currentBaseCity != null) {
    suggestions.push({
      id: 'plan-next-destination',
      message: `Your ${travel.currentBaseCity} stay is planned for ${lifestyle.stayDurationMonths} month${lifestyle.stayDurationMonths === 1 ? '' : 's'} — start shortlisting the next base.`,
      basis: [`stayDurationMonths ${lifestyle.stayDurationMonths}`, 'no next destination recorded'],
      severity: 'info',
    });
  }

  return suggestions;
}
